/**
 * Domain vocabulary shared by the API and the web app.
 *
 * These are `as const` objects rather than TS `enum`s so they survive `isolatedModules`,
 * erase cleanly to plain strings in the database, and can be iterated to build select
 * options in the UI without a reverse-mapping dance.
 */
/* -------------------------------------------------------------------------- */
/* Ledger                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * What a ledger account *is*. Every balance-bearing entity in the system — a bank
 * account, the cash drawer, a party, an expense head — is one row in `ledgeraccounts`
 * carrying one of these kinds. That uniformity is what makes the trial balance and
 * balance sheet fall out of a single aggregation.
 */
export const ACCOUNT_KIND = {
    BANK: "BANK",
    CASH: "CASH",
    PARTY: "PARTY",
    EXPENSE: "EXPENSE",
    INCOME: "INCOME",
    SAVINGS: "SAVINGS",
    CHARGE: "CHARGE",
    EQUITY: "EQUITY",
    SUSPENSE: "SUSPENSE",
};
/** The five classical account classes. Determines the normal balance side. */
export const ACCOUNT_CLASS = {
    ASSET: "ASSET",
    LIABILITY: "LIABILITY",
    EQUITY: "EQUITY",
    INCOME: "INCOME",
    EXPENSE: "EXPENSE",
};
export const ACCOUNT_KIND_CLASS = {
    BANK: "ASSET",
    CASH: "ASSET",
    // A party account swings between asset (they owe us) and liability (we owe them).
    // It is classified as ASSET so debits increase it, and the sign of the balance tells
    // you which side of the relationship you are on. See `PartyBalanceDirection`.
    PARTY: "ASSET",
    EXPENSE: "EXPENSE",
    INCOME: "INCOME",
    SAVINGS: "LIABILITY",
    CHARGE: "EXPENSE",
    EQUITY: "EQUITY",
    SUSPENSE: "ASSET",
};
export const DIRECTION = {
    DEBIT: "DEBIT",
    CREDIT: "CREDIT",
};
/**
 * Which side increases the balance of an account class.
 * ASSET and EXPENSE grow on the debit side; LIABILITY, EQUITY and INCOME on the credit side.
 */
export const NORMAL_SIDE = {
    ASSET: "DEBIT",
    EXPENSE: "DEBIT",
    LIABILITY: "CREDIT",
    EQUITY: "CREDIT",
    INCOME: "CREDIT",
};
/** +1 if the entry increases the account's balance, -1 if it decreases it. */
export function signFor(accountClass, direction) {
    return NORMAL_SIDE[accountClass] === direction ? 1 : -1;
}
/* -------------------------------------------------------------------------- */
/* Transactions                                                               */
/* -------------------------------------------------------------------------- */
export const TRANSACTION_TYPE = {
    PAYMENT_IN: "PAYMENT_IN",
    PAYMENT_OUT: "PAYMENT_OUT",
    BANK_TRANSFER: "BANK_TRANSFER",
    PARTY_TRANSFER: "PARTY_TRANSFER",
    EXPENSE: "EXPENSE",
    INCOME: "INCOME",
    ADJUSTMENT: "ADJUSTMENT",
    SETTLEMENT: "SETTLEMENT",
    SAVINGS: "SAVINGS",
    OPENING_BALANCE: "OPENING_BALANCE",
};
export const TRANSACTION_TYPE_LABEL = {
    PAYMENT_IN: "Payment In",
    PAYMENT_OUT: "Payment Out",
    BANK_TRANSFER: "Bank to Bank",
    PARTY_TRANSFER: "Party to Party",
    EXPENSE: "Expense",
    INCOME: "Income",
    ADJUSTMENT: "Adjustment",
    SETTLEMENT: "Settlement",
    SAVINGS: "Bachat Khata",
    OPENING_BALANCE: "Opening Balance",
};
/**
 * Document number prefixes. `EXP-2026-000001`, `BANK-TRF-2026-000001`, and so on.
 * The fiscal year and a zero-padded, gap-free sequence are appended by the numbering
 * service using an atomic counter.
 */
export const TXN_PREFIX = {
    PAYMENT_IN: "PAY-IN",
    PAYMENT_OUT: "PAY-OUT",
    BANK_TRANSFER: "BANK-TRF",
    PARTY_TRANSFER: "PTY-TRF",
    EXPENSE: "EXP",
    INCOME: "INC",
    ADJUSTMENT: "ADJ",
    SETTLEMENT: "SET",
    SAVINGS: "SAV",
    OPENING_BALANCE: "OPN",
};
/** Prefix for the mirror document created by a reversal. `REV-2026-000001`. */
export const REVERSAL_PREFIX = "REV";
export const TRANSACTION_STATUS = {
    DRAFT: "DRAFT",
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    COMPLETED: "COMPLETED",
    REJECTED: "REJECTED",
    FAILED: "FAILED",
    CANCELLED: "CANCELLED",
    REVERSED: "REVERSED",
};
/**
 * Legal status transitions. The service layer asserts against this map instead of
 * scattering `if (status === ...)` checks through controllers.
 *
 * Ledger entries are written on APPROVED -> COMPLETED and on no other edge. Nothing
 * before COMPLETED has touched a balance, which is why DRAFT and PENDING documents are
 * safely editable and COMPLETED ones are not.
 */
export const TRANSACTION_TRANSITIONS = {
    DRAFT: ["PENDING", "COMPLETED", "CANCELLED"],
    PENDING: ["APPROVED", "REJECTED", "CANCELLED"],
    APPROVED: ["COMPLETED", "FAILED", "CANCELLED"],
    COMPLETED: ["REVERSED"],
    REJECTED: ["DRAFT", "CANCELLED"],
    FAILED: ["PENDING", "CANCELLED"],
    CANCELLED: [],
    REVERSED: [],
};
export function canTransition(from, to) {
    return TRANSACTION_TRANSITIONS[from].includes(to);
}
/** Statuses whose entries are on the books and therefore affect balances and reports. */
export const POSTED_STATUSES = ["COMPLETED", "REVERSED"];
/** Statuses that must never be edited in place — correct them with a reversal instead. */
export const IMMUTABLE_STATUSES = ["COMPLETED", "REVERSED", "CANCELLED"];
/* -------------------------------------------------------------------------- */
/* Payment                                                                    */
/* -------------------------------------------------------------------------- */
export const PAYMENT_MODE = {
    CASH: "CASH",
    BANK_TRANSFER: "BANK_TRANSFER",
    UPI: "UPI",
    NEFT: "NEFT",
    RTGS: "RTGS",
    IMPS: "IMPS",
    CHEQUE: "CHEQUE",
    CARD: "CARD",
    OTHER: "OTHER",
};
export const PAYMENT_MODE_LABEL = {
    CASH: "Cash",
    BANK_TRANSFER: "Bank Transfer",
    UPI: "UPI",
    NEFT: "NEFT",
    RTGS: "RTGS",
    IMPS: "IMPS",
    CHEQUE: "Cheque",
    CARD: "Card",
    OTHER: "Other",
};
/** Modes that settle through a bank account rather than the cash drawer. */
export const BANK_MODES = [
    "BANK_TRANSFER", "UPI", "NEFT", "RTGS", "IMPS", "CHEQUE", "CARD",
];
/* -------------------------------------------------------------------------- */
/* Parties                                                                    */
/* -------------------------------------------------------------------------- */
export const PARTY_TYPE = {
    CUSTOMER: "CUSTOMER",
    VENDOR: "VENDOR",
    DISTRIBUTOR: "DISTRIBUTOR",
    AGENT: "AGENT",
    EMPLOYEE: "EMPLOYEE",
    OTHER: "OTHER",
};
export const PARTY_TYPE_LABEL = {
    CUSTOMER: "Customer",
    VENDOR: "Vendor",
    DISTRIBUTOR: "Distributor",
    AGENT: "Agent",
    EMPLOYEE: "Employee",
    OTHER: "Other",
};
/**
 * Which way a party balance points, in the Digital Khata's own vocabulary.
 *
 * A positive party balance means they owe us — LENA HAI ("to receive").
 * A negative balance means we owe them — DENA HAI ("to give").
 */
export const KHATA_DIRECTION = {
    LENA: "LENA",
    DENA: "DENA",
    CLEAR: "CLEAR",
};
export const KHATA_LABEL = {
    LENA: "Lena Hai",
    DENA: "Dena Hai",
    CLEAR: "Clear",
};
export function khataDirection(balancePaise) {
    if (balancePaise > 0)
        return "LENA";
    if (balancePaise < 0)
        return "DENA";
    return "CLEAR";
}
/* -------------------------------------------------------------------------- */
/* Banking                                                                    */
/* -------------------------------------------------------------------------- */
export const BANK_ACCOUNT_TYPE = {
    CURRENT: "CURRENT",
    SAVINGS: "SAVINGS",
    OD: "OD",
    CC: "CC",
    WALLET: "WALLET",
    OTHER: "OTHER",
};
export const RECON_LINE_STATUS = {
    MATCHED: "MATCHED",
    UNMATCHED: "UNMATCHED",
    MISSING_IN_SYSTEM: "MISSING_IN_SYSTEM",
    MISSING_IN_BANK: "MISSING_IN_BANK",
    DUPLICATE: "DUPLICATE",
    NEEDS_REVIEW: "NEEDS_REVIEW",
};
export const RECON_LINE_STATUS_LABEL = {
    MATCHED: "Matched",
    UNMATCHED: "Unmatched",
    MISSING_IN_SYSTEM: "Missing in System",
    MISSING_IN_BANK: "Missing in Bank",
    DUPLICATE: "Duplicate",
    NEEDS_REVIEW: "Needs Review",
};
/* -------------------------------------------------------------------------- */
/* Charges                                                                    */
/* -------------------------------------------------------------------------- */
export const CHARGE_TYPE = {
    PERCENTAGE: "PERCENTAGE",
    FIXED: "FIXED",
    TIERED: "TIERED",
};
/** Who absorbs the charge — it changes which side of the entry the charge lands on. */
export const CHARGE_BEARER = {
    /** We pay it; it is our expense and reduces the source account by more than the gross. */
    SELF: "SELF",
    /** The party pays it; the gross is reduced and the charge becomes our income. */
    PARTY: "PARTY",
};
/* -------------------------------------------------------------------------- */
/* Adjustments                                                                */
/* -------------------------------------------------------------------------- */
export const ADJUSTMENT_TYPE = {
    OPENING: "OPENING",
    BALANCE_CORRECTION: "BALANCE_CORRECTION",
    BANK: "BANK",
    CASH: "CASH",
    PARTY: "PARTY",
    EXPENSE: "EXPENSE",
    WRITE_OFF: "WRITE_OFF",
};
/* -------------------------------------------------------------------------- */
/* Cash tally                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Daily cash tally outcome.
 *
 * Per §62 the system never "fixes" a mismatch by moving the expectation. It reports
 * SHORT or EXCESS and the difference stands until a human posts an explaining entry.
 */
export const TALLY_STATUS = {
    PENDING: "PENDING",
    MATCHED: "MATCHED",
    SHORT: "SHORT",
    EXCESS: "EXCESS",
};
export function tallyStatus(differencePaise) {
    if (differencePaise === 0)
        return "MATCHED";
    return differencePaise < 0 ? "SHORT" : "EXCESS";
}
/* -------------------------------------------------------------------------- */
/* Approvals                                                                  */
/* -------------------------------------------------------------------------- */
export const APPROVAL_STATUS = {
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    REJECTED: "REJECTED",
    SKIPPED: "SKIPPED",
};
export const APPROVER_TIER = {
    BRANCH_ADMIN: "BRANCH_ADMIN",
    SUPER_ADMIN: "SUPER_ADMIN",
};
/* -------------------------------------------------------------------------- */
/* Settlement                                                                 */
/* -------------------------------------------------------------------------- */
export const SETTLEMENT_KIND = {
    PARTY: "PARTY",
    BANK: "BANK",
};
export const SETTLEMENT_STATUS = {
    PENDING: "PENDING",
    PARTIAL: "PARTIAL",
    COMPLETED: "COMPLETED",
    CANCELLED: "CANCELLED",
};
/* -------------------------------------------------------------------------- */
/* Savings (Bachat Khata)                                                     */
/* -------------------------------------------------------------------------- */
export const SAVINGS_OPERATION = {
    DEPOSIT: "DEPOSIT",
    WITHDRAWAL: "WITHDRAWAL",
    INTEREST: "INTEREST",
    BONUS: "BONUS",
    ADJUSTMENT: "ADJUSTMENT",
};
/* -------------------------------------------------------------------------- */
/* Credit aging                                                               */
/* -------------------------------------------------------------------------- */
export const AGING_BUCKETS = [
    { key: "current", label: "0–30 days", from: 0, to: 30 },
    { key: "b31_60", label: "31–60 days", from: 31, to: 60 },
    { key: "b61_90", label: "61–90 days", from: 61, to: 90 },
    { key: "b90plus", label: "90+ days", from: 91, to: Number.POSITIVE_INFINITY },
];
export function agingBucket(daysOverdue) {
    if (daysOverdue <= 30)
        return "current";
    if (daysOverdue <= 60)
        return "b31_60";
    if (daysOverdue <= 90)
        return "b61_90";
    return "b90plus";
}
/* -------------------------------------------------------------------------- */
/* Lifecycle                                                                  */
/* -------------------------------------------------------------------------- */
export const RECORD_STATUS = {
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE",
    BLOCKED: "BLOCKED",
};
export const PERIOD_STATUS = {
    OPEN: "OPEN",
    CLOSED: "CLOSED",
    LOCKED: "LOCKED",
};
/* -------------------------------------------------------------------------- */
/* Audit                                                                      */
/* -------------------------------------------------------------------------- */
export const AUDIT_ACTION = {
    LOGIN: "LOGIN",
    LOGIN_FAILURE: "LOGIN_FAILURE",
    LOGOUT: "LOGOUT",
    PASSWORD_CHANGE: "PASSWORD_CHANGE",
    PASSWORD_RESET: "PASSWORD_RESET",
    ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
    CREATE: "CREATE",
    UPDATE: "UPDATE",
    DELETE_REQUEST: "DELETE_REQUEST",
    DELETE: "DELETE",
    SUBMIT: "SUBMIT",
    APPROVE: "APPROVE",
    REJECT: "REJECT",
    POST: "POST",
    REVERSE: "REVERSE",
    ROLE_CHANGE: "ROLE_CHANGE",
    PERMISSION_CHANGE: "PERMISSION_CHANGE",
    BALANCE_ADJUSTED: "BALANCE_ADJUSTED",
    RECONCILED: "RECONCILED",
    PERIOD_CLOSED: "PERIOD_CLOSED",
    PERIOD_REOPENED: "PERIOD_REOPENED",
    EXPORT: "EXPORT",
    IMPORT: "IMPORT",
    SETTINGS_UPDATED: "SETTINGS_UPDATED",
};
/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */
export const NOTIFICATION_TYPE = {
    PAYMENT_RECEIVED: "PAYMENT_RECEIVED",
    PAYMENT_SENT: "PAYMENT_SENT",
    APPROVAL_REQUIRED: "APPROVAL_REQUIRED",
    APPROVAL_COMPLETED: "APPROVAL_COMPLETED",
    APPROVAL_REJECTED: "APPROVAL_REJECTED",
    EXPENSE_CREATED: "EXPENSE_CREATED",
    TRANSACTION_REVERSED: "TRANSACTION_REVERSED",
    RECONCILIATION_ISSUE: "RECONCILIATION_ISSUE",
    LOW_BALANCE: "LOW_BALANCE",
    CREDIT_OVERDUE: "CREDIT_OVERDUE",
    CASH_TALLY_MISMATCH: "CASH_TALLY_MISMATCH",
};
export const SEVERITY = {
    INFO: "INFO",
    SUCCESS: "SUCCESS",
    WARNING: "WARNING",
    DANGER: "DANGER",
};
//# sourceMappingURL=enums.js.map